import { useDebounceFn, useInterval } from 'ahooks'
import { Breadcrumb, Radio } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { API } from '@/api'
import LeftArrow from '@/assets/icons/left-arrow.svg?react'
import { Divider, Loading, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView.tsx'
import { cn } from '@/utils/style.ts'
import { TIME_UNITS_IN_SECONDS } from '@/utils/time'

import { extractTimePoints } from './chart.ts'
import { buildChartItemData, Chart, MonitorItemData } from './MonitorChart.tsx'

export function ScalingMonitorDetailSubPage() {
  const { t } = useTranslation()
  const [initialLoading, setInitialLoading] = useState(true)
  const location = useLocation()
  const [monitorDataList, setMonitorDataList] = useState<MonitorItemData[]>([])
  const [filterDuration, setFilterDuration] = useState(TIME_UNITS_IN_SECONDS.HOUR)
  const { scalingSpecId, scaleSpec } = location.state || {}

  const fetchMetrix = () => {
    API.getScaleMetricList({
      spec_id: scalingSpecId,
    })
      .then((res: any) => {
        if (res.code === 0) {
          const timePoints = extractTimePoints(res.matrix || [])
          const formattedDataList = (res.matrix || []).map((item: any) =>
            buildChartItemData(item, timePoints),
          )
          setMonitorDataList(formattedDataList)
        }
      })
      .catch(console.error)
      .finally(() => {
        setInitialLoading(false)
      })
  }

  const debouncedFetchData = useDebounceFn(fetchMetrix, {
    wait: 500,
  })

  useInterval(
    () => {
      debouncedFetchData.run()
    },
    3000,
    { immediate: true },
  )

  useEffect(() => {
    debouncedFetchData.run()
  }, [])

  return (
    <div className="card bg-white">
      <div className="flex items-center space-x-4 mb-4">
        <Link to="/scaling">
          <div className="w-11 h-11 icon-btn">
            <LeftArrow />
          </div>
        </Link>
        <Breadcrumb separator={<span className="h6">/</span>}>
          <Breadcrumb.Item className="h6">
            <Link to="/scaling" className="h6 text-secondary !hover:text-red-600">
              <Trans i18nKey="scaling.title">Autoscaling</Trans>
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span className="h6 text-primary">
              <Trans i18nKey="scaling.monitorDetailSubPage.title">Monitor Detail</Trans>
            </span>
          </Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <Divider />

      <div className="h4 mb-1">
        {scalingSpecId} - {scaleSpec.class}
      </div>

      <div
        className={cn('t-sm mb-8', {
          'text-success': scaleSpec.enabled,
          'text-error': !scaleSpec.enabled,
        })}
      >
        {scaleSpec.enabled ? t('scaling.status.running') : t('scaling.status.suspended')}
      </div>

      <div className="flex justify-between items-start mb-8">
        <div className="h6 text-primary">
          <Trans i18nKey="scaling.loadMetricLabel">Load Metrics</Trans>
        </div>
        <Radio.Group
          onChange={(e) => {
            setFilterDuration(e.target.value)
          }}
          value={filterDuration}
        >
          <Radio.Button value={TIME_UNITS_IN_SECONDS.HOUR}>
            <Trans i18nKey="scaling.monitorDetailSubPage.realTimeBtn">Real-Time</Trans>
          </Radio.Button>
        </Radio.Group>
      </div>

      {initialLoading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}

      {!initialLoading && (
        <>
          {Array.isArray(monitorDataList) && monitorDataList.length > 0 ? (
            <div className="text-tertiary gap-8 grid grid-cols-2">
              {monitorDataList.map((item, index) => (
                <div key={`metric-${index}`}>
                  <div className="mb-2 flex justify-between items-center">
                    <div className="gap-1 t-sb text-primary self-start">{item.name}</div>
                    {item.realtime}
                  </div>
                  <Chart value={item}></Chart>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-16 overflow-hidden mt-4 text-center text-tertiary t-sm">
              <EmptyView
                title={
                  <span className="t-sm text-secondary">
                    No monitoring data is currently available for this instance
                  </span>
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
